import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Chip,
  Menu,
  MenuItem,
  Fade,
  Collapse,
  Alert,
  TextField,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SectionTitle from '../../../components/common/SectionTitle';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import ClientService from '../../../services/ClientService';
import moment from 'moment';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import ClassDisplay from './ClassDisplay';
import _ from 'underscore';
import { trackEvent, handleTrackError } from '../../../services/AmplitudeService';
import CurrentClientProvider from '../../../providers/CurrentClientProvider';
import LocationService from '../../../services/LocationService';

type ClientLocationOption = {
	id?: number | string;
	client_location_id?: number | string;
	location_code?: string;
	location_name?: string;
	name?: string;
	code?: string;
};

const jss = {
	tableLabel: {
		fontWeight: 'bolder',
		width: '25%',
		textAlign: 'right'
	},
	editTitle: {
		fontSize: 16,
		fontWeight: 'bolder',
		mt: 3,
	},
	tagSection: {
		'.MuiChip-root': {
			mr:1,
			mt:1,
		}
	}
}


function ClientClassEdit(props: any) {
	const {
		locations,
		clientClass,
		allClasses,
		masterCategories,
		labelData,
		categories,
		isAdmin,
		onDeleteChange,
		tip_context,
	} = props
	const isV3Variant = props?.visualVariant === 'v3'
	// const [clientClass, setClientClass] = useState()
	const [loading, setLoading] = useState(false)
	const [editing, setEditing] = useState(false)
	const [saving, setSaving] = useState(false)
	const [has_change, setHasChange] = useState(false)
	const [open_more, setOpenMore] = useState(false)
	const more_element = useRef<HTMLButtonElement | null>(null)
	const [formData, setFormData] = useState<any>()
	const currentClientContext: any = CurrentClientProvider.useClient()
	// const [labelData, setLabelData] = useState()
	// const [categories, setCategories] = useState()
	// const [masterCategories, setMasterCategories] = useState([])

	const resolveLocationId = (location: ClientLocationOption | null | undefined): string => {
		if( !location ) {
			return ''
		}
		const rawId = location.client_location_id ?? location.id
		return rawId === undefined || rawId === null ? '' : String(rawId)
	}

	const getLocationLabel = (location: ClientLocationOption | null | undefined): string => {
		if( !location ) {
			return ''
		}
		const id = resolveLocationId(location)
		const code = location.location_code ?? location.code
		const name = location.location_name ?? location.name
		if( code && name ) {
			return `[${code}] ${name}`
		}
		return name || code || (id ? `Location ${id}` : '')
	}

	const mapClassLocationLinksToOptions = (classData: any): ClientLocationOption[] => {
		const locationMap = new Map<string, ClientLocationOption>()
		;(locations || []).forEach((location: ClientLocationOption) => {
			const id = resolveLocationId(location)
			if( id ) {
				locationMap.set(id, location)
			}
		})

		return (classData?.location_links || []).map((link: any) => {
			const locationId = String(link.client_location_id ?? link.client_locationid ?? '')
			if( locationMap.has(locationId) ) {
				return locationMap.get(locationId) as ClientLocationOption
			}

			return {
				client_location_id: link.client_location_id,
				location_code: link.location_code,
				location_name: link.location_name,
				name: link.location_name,
				code: link.location_code,
			}
		})
	}

	const getLocationIdsFromClassLinks = (classData: any): string[] => {
		return (classData?.location_links || []).map((link: any) => {
			return String(link.client_location_id ?? link.client_locationid ?? '')
		}).filter((id: string) => !!id)
	}

	const getSelectedLocationIds = (): string[] => {
		return (formData?.locations || []).map((location: ClientLocationOption) => {
			return resolveLocationId(location)
		}).filter((id: string) => !!id)
	}

	useEffect(() => {
		if( clientClass ) {
			setEditing(false)
			setHasChange(false)
			setFormData({
				code: clientClass.code,
				name: clientClass.name,
				service_type_id: clientClass.service_type_id,
				warehouse_location_id: clientClass.warehouse_location_id,
				labels: clientClass.labels?.map((i: any) => { delete (i.pivot); return i; }),
				categories: clientClass?.categories?.map((i: any) => { delete (i.pivot); return i; }) || [],
				parent_class_id: clientClass.parent_class_id,
				imu: clientClass.imu,
				child_classes: clientClass.child_classes || [],
				master_classes: clientClass?.master_classes?.map((i: any) => { delete (i.pivot); return i; }) || [],
				locations: mapClassLocationLinksToOptions(clientClass),
				LocationClassModelId: clientClass.LocationClassModelId,
			})
		}
	}, [clientClass])

	useEffect(() => {
		if( props.clientClass ) {
			// loadClass()
		}
	}, [props.clientClass])

	useEffect(() => {
		if( !clientClass || !formData || has_change ) {
			return
		}

		setFormData((prev: any) => {
			if( !prev ) {
				return prev
			}

			return {
				...prev,
				locations: mapClassLocationLinksToOptions(clientClass),
			}
		})
	}, [locations, clientClass, has_change])

	async function loadClass() {
		setLoading(true)
		// const data = await ClientService.getClientClassById(props.clientClass.client_id, props.clientClass.id, {
		// 	include: [
		// 		'parentClass',
		// 		'locationLinks',
		// 		'labels',
		// 		'categories',
		// 		'warehouseLocation',
		// 		'childClasses',
		// 		'masterClasses',
		// 	]
		// })
		// setClientClass(data)
		setLoading(false)
	}

	// async function loadLabels() {
	// 	const data = await LabelService.getLabels()
	// 	setLabelData(data)
	// }
	//
	// async function loadCategories() {
	// 	const data = await ClientService.getClientCategories(clientClass.client_id)
	// 	setCategories(data)
	// }
	//
	// async function loadMasterCategories() {
	// 	const data = await ClientService.getMasterCategories();
	// 	setMasterCategories(data)
	// }

	async function undeleteData() {
		try {
			await ClientService.undeleteClassById(clientClass.client_id, clientClass.id)
			setOpenMore(false)
			
			// update the current class with deleted_at and isActive status
			let newClientClass = {...clientClass}
			newClientClass.deleted_at = null
			newClientClass.isActive = 1

			props.onDeleteChange(newClientClass)

			trackEvent('Client Classes Event', {
				action: 'Undeleted Class',
				clientCode: currentClientContext?.client?.ClientCode,
			})
		} catch (e: any) {
			console.log(e)

			if( tip_context && e.response?.status === 400 && e.response?.data.message ) {
				tip_context.show(e.response?.data.message)
			}

			handleTrackError({ error: e, pageName: 'Client Classes', additionalMetadata: { clientCode: currentClientContext?.client?.ClientCode, action: 'Undelete Class' } })
		}
	}

	async function deleteData() {
		try {
			await ClientService.deleteClassById(clientClass.client_id, clientClass.id)
			setOpenMore(false)

			// update the current class with deleted_at and isActive status
			let newClientClass = {...clientClass}
			newClientClass.deleted_at = moment().format('YYYY-MM-DD HH:mm:ss')
			newClientClass.isActive = 0

			props.onDeleteChange(newClientClass)

			trackEvent('Client Classes Event', {
				action: 'Deleted Class',
				clientCode: currentClientContext?.client?.ClientCode,
			})
		} catch (e: any) {
			console.log(e)

			if( tip_context && e.response?.status === 400 && e.response?.data.message ) {
				tip_context.show(e.response?.data.message)
			}

			handleTrackError({ error: e, pageName: 'Client Classes', additionalMetadata: { clientCode: currentClientContext?.client?.ClientCode, action: 'Delete Class' } })
		}
	}

	const masterClasses = useMemo(() => {
		const safeMasterCategories = masterCategories || [];
		let t: any[] = [];
		safeMasterCategories.forEach((mc: any) => {
			t = [...t, ...(mc.master_classes || [])]
		})
		return _.sortBy(t, 'id');
	}, [masterCategories]);

	const common_input_props: any = {
		fullWidth: true,
		variant: 'outlined' as const,
		size: 'small' as const,
		onChange: (e: any) => updateFormData(e),
		sx: { mt: 2 },
	}

	function updateFormData(e: any) {
		setFormData({
			...formData,
			[e.target.name]: e.target.value
		})
		if( !has_change ) {
			setHasChange(true)
		}
	}

	function labelChange(event: any, newValue: any) {
		updateFormData({
			target: {
				name: 'labels',
				value: newValue
			}
		})
	}

	function categoryChange(event: any, newValue: any) {
		updateFormData({
			target: {
				name: 'categories',
				value: newValue ? [newValue] : []
			}
		})
	}

	function locationChange(event: any, newValue: ClientLocationOption[]) {
		updateFormData({
			target: {
				name: 'locations',
				value: newValue
			}
		})
	}

	function childClassChange(event: any, newValue: any) {
		updateFormData({
			target: {
				name: 'child_classes',
				value: newValue
			}
		})
	}

	function imuOptionsChange(event: any, newValue: any) {
		updateFormData({
			target: {
				name: 'LocationClassModelId',
				value: newValue
			}
		})
	}

	function masterClassChange(e: any, newValue: any) {
		updateFormData({
			target: {
				name: 'master_classes',
				value: newValue
			}
		})
	}

	async function saveClass() {
		// if old class service type is in plannable ids and new is not, make sure user is sure
		const plannableIds = ["1", "2"]
		if (plannableIds.includes(clientClass.service_type_id) && !plannableIds.includes(formData.service_type_id)) {
			if( !window.confirm('You are changing the service type from plannable to not plannable, this will remove all the plan data. Are you sure?') ) {
				return
			}
		}
		
		try {
			setSaving(true)
			const selectedCategories = Array.isArray(formData.categories) ? formData.categories : []
			const updatedClass = await ClientService.updateClassById(clientClass.client_id, clientClass.id, {
				...formData, 
				labels: formData.labels.map((i: any) => {return i.id}),
				categories: selectedCategories.map((i: any) => {return i.Id ?? i.id}),
				child_classes: formData.child_classes.map((i: any) => {return i.id}),
				master_classes: formData.master_classes.map((i: any) => {return i.id}),
				locations: getSelectedLocationIds(),
			})

			const originalLocationIds = new Set(getLocationIdsFromClassLinks(clientClass))
			const nextLocationIds = new Set(getSelectedLocationIds())

			const locationIdsToLink = Array.from(nextLocationIds).filter((id: string) => {
				return !originalLocationIds.has(id)
			})
			const locationIdsToUnlink = Array.from(originalLocationIds).filter((id: string) => {
				return !nextLocationIds.has(id)
			})

			if( locationIdsToLink.length || locationIdsToUnlink.length ) {
				await Promise.all([
					...locationIdsToLink.map((locationId: string) => {
						return LocationService.linkLocationClass(clientClass.client_id, locationId, clientClass.id)
					}),
					...locationIdsToUnlink.map((locationId: string) => {
						return LocationService.unlinkLocationClass(clientClass.client_id, locationId, clientClass.id)
					}),
				])
			}

			let classToSave = updatedClass
			try {
				classToSave = await ClientService.getClientClassById(clientClass.client_id, clientClass.id, {
					include: [
						'parentClass',
						'locationLinks',
						'labels',
						'categories',
						'warehouseLocation',
						'childClasses',
						'masterClasses',
					]
				})
			} catch (_refreshError) {
			}

			setEditing(false)
			setHasChange(false)
			if( props.onSave ) {
				props.onSave(classToSave)	
			}
		} catch (e: any) {
			console.log(e.response?.data.message)

			if( tip_context && e.response?.status === 400 && e.response?.data.message ) {
				tip_context.show(e.response?.data.message)
			}
		}
		setSaving(false)
	}

	if( clientClass ) {
		return <Paper elevation={2} className={isV3Variant ? 'v3-profile-summary-card' : undefined}>
			<SectionTitle
				label={clientClass.name}
				subLabel="Class profile"
				type="primary"
				customStyles={isV3Variant ? {
					background: 'linear-gradient(135deg, #7F56D9 0%, #9E77ED 100%)',
					borderTopLeftRadius: '24px',
					borderTopRightRadius: '24px',
				} : undefined}
				customTitleStyle={isV3Variant ? {
					fontWeight: 700,
					letterSpacing: '-0.01em',
				} : undefined}
				rightComponent={
					isAdmin ?
						<>
							{!editing && 
								<IconButton onClick={e => setEditing(true)}>
									<ModeEditOutlineOutlinedIcon sx={{color: 'white'}}/>
								</IconButton>
							}
							{editing && 
								<IconButton onClick={e => setEditing(false)}>
									<KeyboardArrowUpOutlinedIcon sx={{color: 'white'}}/>
								</IconButton>
							}
							<IconButton onClick={() => {setOpenMore(true)}} ref={more_element}>
								<MoreVertOutlinedIcon sx={{color: 'white'}} />
							</IconButton>
							<Menu
								open={open_more}
								anchorEl={more_element.current}
								onClose={e => {setOpenMore(false)}}
							>
								{clientClass.deleted_at && 
									<MenuItem dense onClick={undeleteData}>Undelete class</MenuItem>
								}
								{!clientClass.deleted_at && 
									<MenuItem dense onClick={deleteData}>Drop class</MenuItem>
								}
							</Menu>
						</>
					: null}
			/>
			<Box sx={{p:2,pt:0}}>
				{!editing && <ClassDisplay clientClass={clientClass} reloadData={false}/>}
				{editing && labelData &&
					<Fade timeout={500} in>
						<Box>
							<Collapse in={has_change}>
								<Alert sx={{mt:2}} severity="warning">You have unsaved changes, please hit save before continuing.</Alert>
							</Collapse>

							<Box sx={jss.editTitle}>General</Box>
							<TextField 
								{...common_input_props}
								label="Code" 
								name="code"
								value={formData.code || ''}
							/>
							<TextField 
								{...common_input_props}
								label="Name" 
								name="name"
								value={formData.name || ''}
							/>
							<Autocomplete
								multiple
								disableCloseOnSelect
								options={locations || []}
								isOptionEqualToValue={(option: ClientLocationOption, value: ClientLocationOption) => {
									return resolveLocationId(option) === resolveLocationId(value)
								}}
								getOptionLabel={(option: ClientLocationOption) => getLocationLabel(option)}
								renderOption={(props, option: ClientLocationOption) => {
									const optionId = resolveLocationId(option)
									return (
									<li {...props} key={`class_location_${optionId}`}>
										{getLocationLabel(option)}
									</li>
									);
								}}
								value={formData.locations || []}
								filterSelectedOptions
								renderInput={(params) => (
									<TextField
										{...params}
										{...common_input_props}
										label="Locations"
									/>
								)}
								onChange={locationChange}
							/>
							<TextField
								{...common_input_props}
								select
								name="service_type_id"
								value={formData.service_type_id}
								label="Service Type"
							>
		 						<MenuItem value='1'>OTB</MenuItem>
		 						<MenuItem value='2'>PNOTB</MenuItem>
		 						<MenuItem value='3'>NOTB</MenuItem>
		 						<MenuItem value='7'>History</MenuItem>
		 						<MenuItem value='8'>Distress</MenuItem>
		 						<MenuItem value='9'>Warehouse</MenuItem>
		 						<MenuItem value='10'>Child</MenuItem>
							</TextField>
							{formData.labels &&
								<Autocomplete
									multiple
									disableCloseOnSelect
									options={labelData.labels}
									getOptionLabel={(option) => option.name}
									renderOption={(props, option) => {
										return (
										<li {...props} key={`label_${option.id}`}>
											{option.name}
										</li>
										);
									}}
									value={formData.labels}
									filterSelectedOptions
									renderInput={(params) => (
										<TextField
											{...params}
											{...common_input_props}
											label="M1 Tags"
										/>
									)}
									onChange={labelChange}
								/>
							}
							<Autocomplete
								options={categories || []}
								getOptionLabel={(option: any) => option?.Name || ''}
								isOptionEqualToValue={(option: any, value: any) => option?.Id === value?.Id}
								renderOption={(props, option: any) => {
									return (
									<li {...props} key={`category_${option.Id}`}>
										{option.Name}
									</li>
									);
								}}
								value={formData.categories?.[0] ?? null}
								renderInput={(params) => (
									<TextField
										{...params}
										{...common_input_props}
										label="Category"
									/>
								)}
								onChange={categoryChange}
							/>
							{formData.service_type_id=='10' && 
								<TextField
									{...common_input_props}
									select
									name="parent_class_id"
									value={formData.parent_class_id}
									label="Parent Class"
								>
								{
									allClasses.filter((item: any) => {
										return !item.parent_class_id
									}).map((item: any) => {
										return <MenuItem value={item.id} key={`parent_class_${item.id}`}>[{item.code}] {item.name}</MenuItem>
									})
								}
								</TextField>
							}
							{formData.service_type_id!='10' &&
								<Autocomplete
									multiple
									disableCloseOnSelect
									options={allClasses.filter((item: any) => !!item.parent_class_id && clientClass.child_classes.every((child: any) => child.id !== item.id))}
									getOptionLabel={(option: any) => option.name}
									renderOption={(props, option: any) => {
										return (
										<li {...props} key={`child_class_${option.id}`}>
											[{option.code}] {option.name}
										</li>
										);
									}}
									value={formData.child_classes}
									filterSelectedOptions
									onChange={childClassChange}
									renderInput={(params) => (
										<TextField
											{...params}
											{...common_input_props}
											label="Child Classes"
										/>
									)}
									renderTags={(tagValue: any[]) => tagValue.map((option: { id: number | string; name: string }) => (
										<Chip
											key={`child_class_chip_${option.id}`}
											label={option.name}
											disabled={(clientClass.child_classes || []).indexOf(option) !== -1}
										/>
									))}
								/>
							}
							<TextField
								{...common_input_props}
								select
								name="warehouse_location_id"
								value={formData.warehouse_location_id}
								label="Warehouse Location"
							>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
							{locations.map((_location: any) => {
								return <MenuItem key={`Warehouse_location_${_location.client_location_id}`} value={_location.client_location_id}>[{_location.location_code}] {_location.location_name}</MenuItem>
							})}
							</TextField>
							<TextField
								{...common_input_props}
								label="Imu"
								name="imu"
								value={formData.imu || 0}
								type="number"
							/>

							<FormControl>
								<RadioGroup
									aria-labelledby="demo-radio-buttons-group-label"
									value={formData.LocationClassModelId}
									name="radio-buttons-group"
									onChange={imuOptionsChange}
								>
									<FormControlLabel value="0" control={<Radio />} label="Receiving" />
									<FormControlLabel value="1" control={<Radio />} label="On Order" />
									<FormControlLabel value="2" control={<Radio />} label="Plan" />
									<FormControlLabel value="3" control={<Radio />} label="Override" />
								</RadioGroup>
							</FormControl>

							<Autocomplete
								multiple
								options={masterClasses}
								getOptionLabel={(option) => `[${option.id}] ${option.name}`}
								renderOption={(props, option) => {
									return (
									<li {...props} key={`master_class_${option.id}`}>
										[{option.id}] {option.name}
									</li>
									);
								}}
								value={formData.master_classes}
								filterSelectedOptions
								renderInput={(params) => (
									<TextField
										{...params}
										{...common_input_props}
										label="Master Classes"
									/>
								)}
								onChange={masterClassChange}
							/>

							<Box sx={{mt:2}}>
								<LoadingButton loading={saving} size="large" variant="contained" onClick={saveClass} startIcon={<SaveTwoToneIcon />}>
									Save
								</LoadingButton>
							</Box>

						</Box>
					</Fade>
				}
			</Box>
		</Paper>
	} else {
		return <></>
	}
}
export default ClientClassEdit